import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowUpRight,
  User,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  LifeBuoy,
  ShoppingBag,
  Bell,
  Heart,
  ShoppingCart,
  Search,
} from 'lucide-react'
import styles from './Navbar.module.css'
import useScrollSpy from '../hooks/useScrollSpy'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { PRODUCTS, PRODUCT_STATUSES } from '../lib/payments'
import GuestNav from './GuestNav'
import NotificationBell from './NotificationBell'
import LanguageSwitcher from './LanguageSwitcher'
import SearchOverlay from './SearchOverlay'
import ThemeToggle from './ThemeToggle'
import { useI18n } from '../lib/i18n'
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion'

const links = [
  { label: 'nav.home', to: '/#home', anchor: true },
  { label: 'nav.services', to: '/#services', anchor: true },
  { label: 'nav.products', to: '/products', anchor: false, hasDropdown: true },
  { label: 'nav.pricing', to: '/pricing', anchor: false },
  { label: 'nav.about', to: '/#about', anchor: true },
  { label: 'nav.blog', to: '/blog', anchor: false },
  { label: 'nav.integrations', to: '/integrations', anchor: false },
  { label: 'nav.company', to: '/company', anchor: false },
  { label: 'nav.contact', to: '/#contact', anchor: true },
]

const easing = [0.16, 1, 0.3, 1]

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const userMenuRef = useRef(null)
  const dropdownTimeout = useRef(null)
  const megaRef = useRef(null)
  const dropdownTriggerRef = useRef(null)
  const burgerRef = useRef(null)
  const mobileMenuRef = useRef(null)
  const location = useLocation()
  const activeSection = useScrollSpy(['home', 'services', 'about', 'contact'])
  const { user } = useAuth()
  const { t } = useI18n()
  const reduced = usePrefersReducedMotion()

  const panelMotion = reduced
    ? {
        initial: { opacity: 0, x: '-50%' },
        animate: { opacity: 1, x: '-50%' },
        exit: { opacity: 0, x: '-50%' },
        transition: { duration: 0.12 },
      }
    : {
        initial: { opacity: 0, y: 8, x: '-50%' },
        animate: { opacity: 1, y: 0, x: '-50%' },
        exit: { opacity: 0, y: 8, x: '-50%' },
        transition: { duration: 0.2, ease: easing },
      }
  const menuMotion = reduced
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.12 },
      }
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 8 },
        transition: { duration: 0.16, ease: easing },
      }

  const drawerVariants = reduced
    ? {
        hidden: { x: '0%', opacity: 0 },
        show: { x: '0%', opacity: 1, transition: { duration: 0.12 } },
      }
    : {
        hidden: { x: '100%', opacity: 1 },
        show: {
          x: '0%',
          opacity: 1,
          transition: {
            type: 'spring',
            stiffness: 420,
            damping: 42,
            staggerChildren: 0.04,
            delayChildren: 0.08,
          },
        },
      }

  const itemVariants = reduced
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0.12 } },
      }
    : {
        hidden: { opacity: 0, y: 14 },
        show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: easing } },
      }

  const isRouteActive = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`)

  const isAnchorActive = (to) => location.pathname === '/' && activeSection === to.replace('#', '')

  const handleSignOut = async () => {
    if (supabase) await supabase.auth.signOut()
    setUserMenuOpen(false)
    setOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
    }
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userMenuOpen])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    setSearchOpen(false)
    setOpen(false)
    setDropdownOpen(false)
  }, [location])

  useEffect(() => () => clearTimeout(dropdownTimeout.current), [])

  useEffect(() => {
    if (!open) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const node = mobileMenuRef.current
    const burger = burgerRef.current
    const focusables = () =>
      node
        ? Array.from(
            node.querySelectorAll(
              'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => el.offsetParent !== null)
        : []

    const raf = requestAnimationFrame(() => {
      const [first] = focusables()
      ;(first || node)?.focus()
    })

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false)
        return
      }
      if (e.key !== 'Tab') return
      const items = focusables()
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      cancelAnimationFrame(raf)
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
      burger?.focus()
    }
  }, [open])

  const handleDropdownEnter = () => {
    clearTimeout(dropdownTimeout.current)
    setDropdownOpen(true)
  }

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setDropdownOpen(false), 200)
  }

  const handleDropdownKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault()
      setDropdownOpen(true)
    } else if (e.key === 'Escape') {
      setDropdownOpen(false)
    }
  }

  return (
    <header
      className={`${styles.navbar} ${scrolled || open ? styles.scrolled : ''}`}
      data-scrolled={scrolled || open ? 'true' : 'false'}
    >
      <div className={`container ${styles.navContainer}`}>
        <Link to="/" aria-label="OPRAIZ — home" className={styles.brand}>
          <span className={styles.brandWord}>OPRAIZ</span>
          <span className={styles.brandRule} aria-hidden="true" />
        </Link>

        <nav className={styles.navLinks} aria-label="Primary">
          {links.map((link) =>
            link.hasDropdown ? (
              <div
                key={link.label}
                className={styles.dropdownWrap}
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
                onFocus={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget)) return
                  handleDropdownEnter()
                }}
                onBlur={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget)) return
                  handleDropdownLeave()
                }}
              >
                <Link
                  ref={dropdownTriggerRef}
                  to={link.to}
                  className={`${styles.link} ${isRouteActive(link.to) ? styles.active : ''}`}
                  aria-current={isRouteActive(link.to) ? 'page' : undefined}
                  aria-haspopup="true"
                  aria-expanded={dropdownOpen}
                  aria-controls="products-dropdown"
                  onClick={(e) => {
                    if (dropdownOpen) e.preventDefault()
                  }}
                  onKeyDown={handleDropdownKeyDown}
                >
                  {t(link.label)}
                  <ChevronDown
                    className={`${styles.dropdownArrow} ${dropdownOpen ? styles.dropdownArrowOpen : ''}`}
                    size={12}
                    aria-hidden="true"
                  />
                </Link>

                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      ref={megaRef}
                      id="products-dropdown"
                      role="menu"
                      aria-label="Products"
                      className={styles.megaDropdown}
                      {...panelMotion}
                      onMouseEnter={handleDropdownEnter}
                      onMouseLeave={handleDropdownLeave}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setDropdownOpen(false)
                          dropdownTriggerRef.current?.focus()
                          return
                        }
                        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
                        e.preventDefault()
                        const items = Array.from(
                          megaRef.current?.querySelectorAll('[role="menuitem"]') || []
                        )
                        if (!items.length) return
                        const current = document.activeElement
                        const index = items.indexOf(current)
                        const delta = e.key === 'ArrowDown' ? 1 : -1
                        const next = items[(index + delta + items.length) % items.length]
                        next?.focus()
                      }}
                    >
                      <div className={styles.megaHeader}>
                        <span>Our Products</span>
                        <span aria-hidden="true">{PRODUCTS.length} entries</span>
                      </div>
                      <div className={styles.megaGrid}>
                        {PRODUCTS.map((product) => (
                          <Link
                            key={product.id}
                            to={`/products/${product.id}`}
                            role="menuitem"
                            className={styles.megaRow}
                            onClick={() => setDropdownOpen(false)}
                          >
                            <span className={styles.megaRowId} aria-hidden="true">
                              {product.id}
                            </span>
                            <span className={styles.megaRowName}>{product.name}</span>
                            <span className={styles.megaRowTag}>{product.tagline}</span>
                            <span className={styles.megaRowStatus} aria-hidden="true">
                              {PRODUCT_STATUSES[product.status]?.label}
                            </span>
                            <ArrowUpRight
                              size={14}
                              className={styles.megaRowArrow}
                              aria-hidden="true"
                            />
                          </Link>
                        ))}
                      </div>
                      <div className={styles.megaFooter}>
                        <Link
                          to="/products"
                          role="menuitem"
                          className={styles.megaFooterLink}
                          onClick={() => setDropdownOpen(false)}
                        >
                          View All Products <ArrowUpRight size={13} aria-hidden="true" />
                        </Link>
                        <Link
                          to="/build"
                          role="menuitem"
                          className={styles.megaFooterLink}
                          onClick={() => setDropdownOpen(false)}
                        >
                          R&D Services <ArrowUpRight size={13} aria-hidden="true" />
                        </Link>
                        <Link
                          to="/whitelabel"
                          role="menuitem"
                          className={styles.megaFooterLink}
                          onClick={() => setDropdownOpen(false)}
                        >
                          White-Label <ArrowUpRight size={13} aria-hidden="true" />
                        </Link>
                        <Link
                          to="/deploy"
                          role="menuitem"
                          className={styles.megaFooterLink}
                          onClick={() => setDropdownOpen(false)}
                        >
                          Enterprise Deploy <ArrowUpRight size={13} aria-hidden="true" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : link.anchor ? (
              <a
                key={link.label}
                href={link.to}
                className={`${styles.link} ${isAnchorActive(link.to) ? styles.active : ''}`}
                aria-current={isAnchorActive(link.to) ? 'true' : undefined}
              >
                {t(link.label)}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.to}
                className={`${styles.link} ${isRouteActive(link.to) ? styles.active : ''}`}
                aria-current={isRouteActive(link.to) ? 'page' : undefined}
              >
                {t(link.label)}
              </Link>
            )
          )}
        </nav>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.searchTrigger}
            onClick={() => setSearchOpen(true)}
            aria-label={`${t('common.search')} ⌘K`}
          >
            <Search size={16} aria-hidden="true" />
            <span className={styles.searchTriggerText}>{t('common.search')}</span>
            <kbd className={styles.searchKbd} aria-hidden="true">
              ⌘K
            </kbd>
          </button>
          <ThemeToggle />
          <LanguageSwitcher />
          {user ? (
            <div className={styles.userMenuWrap} ref={userMenuRef}>
              <NotificationBell />
              <button
                type="button"
                className={`btn btn-outline ${styles.getStarted} ${styles.userBtn}`}
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <User size={16} aria-hidden="true" /> {t('nav.account')}
                <ChevronDown size={12} aria-hidden="true" />
              </button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    className={styles.userMenu}
                    role="menu"
                    aria-label="Account"
                    {...menuMotion}
                  >
                    <Link
                      to="/dashboard"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <LayoutDashboard size={15} aria-hidden="true" /> Dashboard
                    </Link>
                    <Link
                      to="/account"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User size={15} aria-hidden="true" /> {t('nav.account')}
                    </Link>
                    <Link
                      to="/notifications"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Bell size={15} aria-hidden="true" /> Notifications
                    </Link>
                    <Link
                      to="/support"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <LifeBuoy size={15} aria-hidden="true" /> Support
                    </Link>
                    <Link
                      to="/marketplace"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <ShoppingBag size={15} aria-hidden="true" /> Marketplace
                    </Link>
                    <Link
                      to="/wishlist"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Heart size={15} aria-hidden="true" /> Wishlist
                    </Link>
                    <Link
                      to="/cart"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <ShoppingCart size={15} aria-hidden="true" /> Cart
                    </Link>
                    <button
                      type="button"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={handleSignOut}
                    >
                      <LogOut size={15} aria-hidden="true" /> Sign out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <GuestNav />
          )}
          <button
            ref={burgerRef}
            type="button"
            className={`${styles.burger} ${open ? styles.burgerOpen : ''}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="mobile-navigation"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={mobileMenuRef}
            id="mobile-navigation"
            className={styles.mobileMenu}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            tabIndex={-1}
            initial={open ? 'hidden' : false}
            animate="show"
            exit="hidden"
            variants={drawerVariants}
          >
            <motion.div variants={itemVariants} className={styles.mobileSearchWrap}>
              <button
                type="button"
                className={`${styles.searchTrigger} ${styles.mobileSearchTrigger}`}
                onClick={() => {
                  setOpen(false)
                  setSearchOpen(true)
                }}
              >
                <Search size={16} aria-hidden="true" />
                <span className={styles.searchTriggerText}>{t('common.search_label')}</span>
              </button>
            </motion.div>
            {links.map((link) =>
              link.anchor ? (
                <motion.div key={link.label} variants={itemVariants}>
                  <a
                    href={link.to}
                    className={`${styles.mobileLink} ${isAnchorActive(link.to) ? styles.mobileLinkActive : ''}`}
                    onClick={() => setOpen(false)}
                  >
                    {t(link.label)}
                  </a>
                </motion.div>
              ) : (
                <motion.div key={link.label} variants={itemVariants}>
                  <Link
                    to={link.to}
                    className={`${styles.mobileLink} ${isRouteActive(link.to) ? styles.mobileLinkActive : ''}`}
                    aria-current={isRouteActive(link.to) ? 'page' : undefined}
                    onClick={() => setOpen(false)}
                  >
                    {t(link.label)}
                  </Link>
                </motion.div>
              )
            )}

            <motion.div variants={itemVariants} className={styles.mobileGroup}>
              <p className={styles.mobileGroupLabel}>Products</p>
              {PRODUCTS.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className={styles.mobileSubLink}
                  onClick={() => setOpen(false)}
                >
                  {product.name}
                </Link>
              ))}
            </motion.div>

            <motion.div variants={itemVariants} className={styles.mobileGroup}>
              <p className={styles.mobileGroupLabel}>Services</p>
              <Link to="/build" className={styles.mobileSubLink} onClick={() => setOpen(false)}>
                R&D Services
              </Link>
              <Link
                to="/whitelabel"
                className={styles.mobileSubLink}
                onClick={() => setOpen(false)}
              >
                White-Label
              </Link>
              <Link to="/deploy" className={styles.mobileSubLink} onClick={() => setOpen(false)}>
                Enterprise Deploy
              </Link>
              <Link to="/changelog" className={styles.mobileSubLink} onClick={() => setOpen(false)}>
                {t('footer.changelog')}
              </Link>
              <Link
                to="/demo/schedule"
                className={styles.mobileSubLink}
                onClick={() => setOpen(false)}
              >
                {t('cta.view_demo')}
              </Link>
            </motion.div>

            <motion.div variants={itemVariants} className={styles.mobileTools}>
              <ThemeToggle />
            </motion.div>

            {user ? (
              <motion.div variants={itemVariants}>
                <LanguageSwitcher variant="mobile" />
                <Link
                  to="/dashboard"
                  className={`btn btn-outline ${styles.getStarted}`}
                  onClick={() => setOpen(false)}
                >
                  <User size={16} aria-hidden="true" /> Account
                </Link>
                <button
                  type="button"
                  className={`btn btn-outline ${styles.getStarted}`}
                  onClick={handleSignOut}
                >
                  <LogOut size={16} aria-hidden="true" /> Sign out
                </button>
              </motion.div>
            ) : (
              <motion.div variants={itemVariants}>
                <GuestNav variant="mobile" onNavigate={() => setOpen(false)} />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  )
}

export default Navbar
